import { useState, useEffect, useCallback } from 'react';
import { customInstance } from '@edison/api-client-react';
import { MessageSquare, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import { InlineError } from '@/components/ui/inline-error.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';

// ERA-020: read auditor/advisor findings (created via the portal) inside the tenant so the claim
// owner can see and act on them — previously they were write-only.
interface ClaimFinding {
  id: string;
  content: string;
  severity: 'info' | 'concern' | 'critical' | null;
  createdAt: string;
  reviewerLabel: string | null;
  reviewerRole: string | null;
}

// Icon + text (never colour alone) per the accessibility standard.
const SEVERITY: Record<string, { label: string; cls: string; Icon: typeof Info }> = {
  info: { label: 'Info', cls: 'text-blue-700 bg-blue-50 border-blue-200', Icon: Info },
  concern: { label: 'Concern', cls: 'text-amber-700 bg-amber-50 border-amber-200', Icon: AlertTriangle },
  critical: { label: 'Critical', cls: 'text-red-700 bg-red-50 border-red-200', Icon: AlertOctagon },
};

export function ClaimFindings({ claimId }: { claimId: string }) {
  const [items, setItems] = useState<ClaimFinding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setIsLoading(true);
    setError('');
    customInstance<{ data: { items: ClaimFinding[] } }>({ url: `/api/claims/${claimId}/findings`, method: 'GET' })
      .then((res) => setItems(res.data.items))
      .catch(() => setError('Failed to load auditor findings.'))
      .finally(() => setIsLoading(false));
  }, [claimId]);

  useEffect(() => { load(); }, [load]);

  if (isLoading) {
    return <div className="space-y-2">{[0, 1].map((i) => <SkeletonRow key={i} />)}</div>;
  }
  if (error) {
    return <InlineError message={error} onRetry={load} />;
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<MessageSquare className="h-8 w-8" />}
        title="No auditor findings yet"
        body="Findings raised by auditors or advisors through the portal will appear here."
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-foreground">Auditor & advisor findings</p>
      <div className="divide-y divide-border rounded-lg border border-border">
        {items.map((f) => {
          const sev = f.severity ? SEVERITY[f.severity] : undefined;
          const Icon = sev?.Icon ?? Info;
          return (
            <div key={f.id} className="p-4 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                {sev && (
                  <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${sev.cls}`}>
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    {sev.label}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {f.reviewerLabel ?? 'Reviewer'}{f.reviewerRole ? ` · ${f.reviewerRole}` : ''} · {new Date(f.createdAt).toLocaleDateString('en-AU')}
                </span>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap">{f.content}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
